// Copyright (c) 2026 Joshua S. Plaza. All rights reserved.

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { authAPI } from '../../services/api';

// Safely parse user from sessionStorage
const getUserFromStorage = () => {
  try {
    const userStr = sessionStorage.getItem('user');
    if (!userStr) {
      return null;
    }
    const parsed = JSON.parse(userStr);
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid user payload');
    }
    return parsed;
  } catch (e) {
    // Corrupt session data — treat the whole session as logged out
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    return null;
  }
};

const user = getUserFromStorage();
const token = sessionStorage.getItem('token');

const initialState = {
  user: user || null,
  token: token || null,
  isAuthenticated: !!token,
  loading: false,
  error: null,
};

// Login thunk
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/accounts/login/', { username, password });
      const { access, refresh, user } = response.data;

      sessionStorage.setItem('token', access);
      sessionStorage.setItem('refreshToken', refresh);
      sessionStorage.setItem('user', JSON.stringify(user));

      return { token: access, user };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Login failed. Please check your credentials.'
      );
    }
  }
);

// Logout thunk
export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    const refreshToken = sessionStorage.getItem('refreshToken');
    if (refreshToken) {
      // Blacklist the refresh token on the server
      await authAPI.logout(refreshToken);
    }
  } catch (error) {
    // Continue with logout even if blacklist fails
    if (process.env.NODE_ENV === 'development') console.error('Failed to blacklist token:', error);
  } finally {
    // Always clear local storage
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
  }
  return null;
});

// Get current user thunk
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/accounts/users/me/');
      const user = response.data;
      sessionStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      return rejectWithValue('Failed to get user info');
    }
  }
);

// Change password thunk
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ oldPassword, newPassword, newPasswordConfirm }, { rejectWithValue }) => {
    try {
      await api.post('/accounts/users/change_password/', {
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });
      return { message: 'Password changed successfully' };
    } catch (error) {
      const errData = error.response?.data;
      const msg = typeof errData === 'object'
        ? Object.values(errData).flat().join(' ')
        : errData || 'Failed to change password';
      return rejectWithValue(msg);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Change password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCredentials } = authSlice.actions;
export default authSlice.reducer;
